import ExcelJS from "exceljs";

import { downloadFile } from "./downloadFile";

const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const REPORT_COLUMNS = [
  { header: "templateId", width: 40, get: (v) => v.templateId },
  { header: "templateType", width: 16, get: (v) => v.templateType },
  { header: "name", width: 32, get: (v) => v.name },
  { header: "id", width: 32, get: (v) => v.id },
  { header: "accountName", width: 24, get: (v) => v.accountName },
  { header: "drNumber", width: 16, get: (v) => v.drNumber },
  { header: "customer.name", width: 24, get: (v) => v.customer?.name },
  { header: "createdBy", width: 26, get: (v) => v.createdBy },
  { header: "createdAt", width: 22, get: (v) => v.createdAt },
  { header: "updatedAt", width: 22, get: (v) => v.updatedAt },
  { header: "videoPath", width: 50, get: (v) => v.videoPath },
];

const PROJECTS_LIST_COLUMNS = [
  { header: "Project", width: 32, get: (v) => v.name },
  { header: "ID", width: 36, get: (v) => v.id },
  { header: "Status", width: 14, get: (v) => v.status },
  {
    header: "Current Step",
    width: 22,
    get: (v) => v.currentStepLabel ?? v.currentStep,
  },
  { header: "Created Date", width: 22, get: (v) => v.createdAt },
  { header: "Last Action Date", width: 22, get: (v) => v.updatedAt },
  {
    header: "Customer",
    width: 24,
    get: (v) => v.customerName ?? v.customer?.name,
  },
  { header: "Account", width: 24, get: (v) => v.accountName },
  { header: "DR Number", width: 16, get: (v) => v.drNumber },
  { header: "Template", width: 24, get: (v) => v.templateId },
  { header: "Template Type", width: 16, get: (v) => v.templateType },
  { header: "Created By", width: 28, get: (v) => v.createdBy },
  {
    header: "Shared With",
    width: 40,
    get: (v) =>
      Array.isArray(v.sharedWith)
        ? v.sharedWith.filter(Boolean).join(", ")
        : "",
  },
  { header: "Copy Of", width: 36, get: (v) => v.duplicatedFrom?.id },
  { header: "Rate", width: 10, get: (v) => v.rating },
  { header: "Feedback Message", width: 40, get: (v) => v.feedbackMessage },
  { header: "Comment", width: 40, get: (v) => v.comment },
  { header: "Video Path", width: 50, get: (v) => v.videoPath },
];

const DATE_HEADERS = new Set([
  "createdAt",
  "updatedAt",
  "Created Date",
  "Last Action Date",
]);

const STATUS_RECORD_DATE_FIELDS = new Set([
  "checkedAt",
  "createdAt",
  "updatedAt",
]);

const toDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date;
};

const formatCell = (column, video) => {
  const value = column.get(video);

  if (DATE_HEADERS.has(column.header)) {
    return toDate(value);
  }

  return value ?? "";
};

/**
 * Builds an xlsx workbook from the admin videos report and triggers a browser
 * download.
 *
 * @param {Array<object>} videos - Video rows from the admin videos-report API.
 * @param {string} [fileName] - Optional file name for the download.
 * @returns {Promise<void>}
 */
export const downloadVideosReportExcel = async (videos = [], fileName) => {
  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Videos");

  sheet.addTable({
    name: "Videos",
    ref: "A1",
    headerRow: true,
    totalsRow: false,
    style: {
      theme: "TableStyleMedium2",
      showRowStripes: true,
    },
    columns: REPORT_COLUMNS.map(({ header }) => ({
      name: header,
      filterButton: true,
    })),
    rows: videos.map((video) =>
      REPORT_COLUMNS.map((column) => formatCell(column, video)),
    ),
  });

  REPORT_COLUMNS.forEach((column, index) => {
    sheet.getColumn(index + 1).width = column.width;
  });

  const buffer = await workbook.xlsx.writeBuffer();

  downloadFile(buffer, fileName ?? `videos-${Date.now()}.xlsx`, XLSX_MIME_TYPE);
};

/**
 * Builds an xlsx workbook from the filtered admin status records and triggers a
 * browser download.
 *
 * @param {Array<object>} records - Filtered `status` collection documents.
 * @param {Array<{id: string, name: string}>} columns - Columns to export.
 * @param {string} [fileName] - Optional file name for the download.
 * @returns {Promise<void>}
 */
export const downloadStatusRecordsExcel = async (
  records = [],
  columns = [],
  fileName,
) => {
  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Status records");
  const headers = columns.map(({ name }) => name.trim());

  const toCell = (id, value) => {
    if (value === undefined || value === null) return "";
    if (STATUS_RECORD_DATE_FIELDS.has(id)) return toDate(value);
    if (typeof value === "object") return JSON.stringify(value);
    return value;
  };

  if (records.length > 0) {
    sheet.addTable({
      name: "StatusRecords",
      ref: "A1",
      headerRow: true,
      totalsRow: false,
      style: {
        theme: "TableStyleMedium2",
        showRowStripes: true,
      },
      columns: headers.map((name) => ({ name, filterButton: true })),
      rows: records.map((record) =>
        columns.map(({ id }) => toCell(id, record[id])),
      ),
    });
  } else {
    sheet.addRow(headers);
  }

  columns.forEach((column, index) => {
    sheet.getColumn(index + 1).width = Math.max(
      12,
      Math.round((column.minWidth ?? 160) / 7),
    );
  });

  const buffer = await workbook.xlsx.writeBuffer();

  downloadFile(
    buffer,
    fileName ?? `status-records-${Date.now()}.xlsx`,
    XLSX_MIME_TYPE,
  );
};

/**
 * Builds an xlsx workbook from the filtered admin projects list and triggers a
 * browser download.
 *
 * @param {Array<object>} projects - Filtered project rows from AdminProjects.
 * @param {string} [fileName] - Optional file name for the download.
 * @returns {Promise<void>}
 */
export const downloadProjectsListExcel = async (projects = [], fileName) => {
  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Projects");

  if (projects.length > 0) {
    sheet.addTable({
      name: "Projects",
      ref: "A1",
      headerRow: true,
      totalsRow: false,
      style: {
        theme: "TableStyleMedium2",
        showRowStripes: true,
      },
      columns: PROJECTS_LIST_COLUMNS.map(({ header }) => ({
        name: header,
        filterButton: true,
      })),
      rows: projects.map((project) =>
        PROJECTS_LIST_COLUMNS.map((column) => formatCell(column, project)),
      ),
    });
  } else {
    sheet.addRow(PROJECTS_LIST_COLUMNS.map(({ header }) => header));
  }

  PROJECTS_LIST_COLUMNS.forEach((column, index) => {
    sheet.getColumn(index + 1).width = column.width;
  });

  const buffer = await workbook.xlsx.writeBuffer();

  downloadFile(
    buffer,
    fileName ?? `projects-${Date.now()}.xlsx`,
    XLSX_MIME_TYPE,
  );
};
